import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "dragStart",
    "dragEnd",
    "startPanel",
    "endPanel",
    "mainPanel",
  ];

  static values = {
    // START PANEL
    startSnapable: Boolean,
    startMinSize: Number,
    startSnappedSize: Number,
    startSize: String,
    startHidden: Boolean,

    // END PANEL
    endSnapable: Boolean,
    endMinSize: Number,
    endClosedSize: Number,
    endSize: String,
    endHidden: Boolean,

    // GLOBAL SNAP CONFIG
    snapCloseFinal: Number,
    snapOpenThreshold: Number,

    // ORIENTATION
    orientation: String, // "horizontal" (default) or "vertical"
  };

  // Helper: check orientation
  get isVertical() {
    return this.orientationValue === "vertical";
  }

  connect() {
    this.proposedStartSize = this.startSizeValue;
    this.startSize = this.startSizeValue;
    this.proposedEndSize = this.endSizeValue;
    this.endSize = this.endSizeValue;
    this.boundUpdateSize = this.updateSize.bind(this);
    this.startHidden = this.startHiddenValue;
    this.endHidden = this.endHiddenValue;

    // Set initial CSS properties based on hidden state
    if (this.startHidden) {
      this.element.style.setProperty("--start-panel-size", "0px");
    }
    if (this.endHidden) {
      this.element.style.setProperty("--end-panel-size", "0px");
    }
  }

  // Helper getters
  isSnapable(side) {
    return side === "start" ? this.startSnapableValue : this.endSnapableValue;
  }
  getMinSize(side) {
    return side === "start" ? this.startMinSizeValue : this.endMinSizeValue;
  }
  getClosedSize(side) {
    return side === "start"
      ? this.startSnappedSizeValue
      : this.endClosedSizeValue;
  }
  getPanelSize(side) {
    return side === "start" ? this.startSizeValue : this.endSizeValue;
  }

  // --- SNAP TOGGLE ---
  toggleStartPanel() {
    if (this.isSnapable("start")) {
      const snapped = this.element.classList.toggle("start-panel-snapped");
      const size = snapped ? this.startSnappedSizeValue : this.startSizeValue;
      this.element.style.setProperty("--start-panel-size", `${size}px`);
    } else {
      // For non-snapable panels, hide/show completely
      this.startHidden = !this.startHidden;
      if (this.startHidden) {
        this.element.style.setProperty("--start-panel-size", "0px");
        this.element.classList.add("start-panel-hidden");
      } else {
        this.element.style.setProperty(
          "--start-panel-size",
          this.startSizeValue,
        );
        this.element.classList.remove("start-panel-hidden");
      }
    }
  }

  toggleEndPanel() {
    if (this.isSnapable("end")) {
      const snapped = this.element.classList.toggle("end-panel-snapped");
      const size = snapped ? this.endClosedSizeValue : this.endSizeValue;
      this.element.style.setProperty("--end-panel-size", `${size}px`);
    } else {
      // For non-snapable panels, hide/show completely
      this.endHidden = !this.endHidden;
      if (this.endHidden) {
        this.element.style.setProperty("--end-panel-size", "0px");
        this.element.classList.add("end-panel-hidden");
      } else {
        this.element.style.setProperty("--end-panel-size", this.endSizeValue);
        this.element.classList.remove("end-panel-hidden");
      }
    }
  }

  showEndPanel() {
    if (this.endHidden) {
      this.endHidden = false;
      this.element.style.setProperty("--end-panel-size", this.endSizeValue);
      this.element.classList.remove("end-panel-hidden");
    }
  }

  hideEndPanel() {
    if (!this.endHidden) {
      this.endHidden = true;
      this.element.style.setProperty("--end-panel-size", "0px");
      this.element.classList.add("end-panel-hidden");
    }
  }

  updateSize() {
    if (this.proposedStartSize !== this.startSize) {
      this.element.style.setProperty(
        "--start-panel-size",
        `${this.proposedStartSize}px`,
      );
      this.startSize = this.proposedStartSize;
    }
    if (this.proposedEndSize !== this.endSize) {
      this.element.style.setProperty(
        "--end-panel-size",
        `${this.proposedEndSize}px`,
      );
      this.endSize = this.proposedEndSize;
    }
  }

  // --- START PANEL DRAG ---
  startStart(event) {
    event.preventDefault();
    const snapable = this.isSnapable("start");
    // CHANGED: orientation-aware pointer and size
    let initialPos = this.isVertical ? event.clientY : event.clientX;
    let initialStartSize = parseInt(
      getComputedStyle(this.startPanelTarget)[
        this.isVertical ? "height" : "width"
      ],
      10,
    );
    this.dragStartTarget.classList.add("dragging");
    const startedAt = Date.now();

    const stopDrag = () => {
      this.dragStartTarget.classList.remove("dragging");
      document.removeEventListener("mousemove", dragStart);
      document.removeEventListener("mouseup", stopDrag);
      if (snapable && Date.now() - startedAt < 200) this.toggleStartPanel();
    };

    const dragStart = (event) => {
      // CHANGED: orientation-aware pointer
      let pointer = this.isVertical ? event.clientY : event.clientX;
      let newSize = initialStartSize + (pointer - initialPos);

      if (snapable) {
        // Snap closed
        if (newSize < this.snapCloseFinalValue) {
          this.element.classList.add("start-panel-snapped");
          this.element.style.setProperty(
            "--start-panel-size",
            `${this.startSnappedSizeValue}px`,
          );
          this.proposedStartSize = this.startSnappedSizeValue;
          stopDrag();
          return;
        }
        // Snap open
        if (
          this.element.classList.contains("start-panel-snapped") &&
          newSize > this.snapOpenThresholdValue
        ) {
          this.element.classList.remove("start-panel-snapped");
          this.element.style.setProperty(
            "--start-panel-size",
            `${this.startSizeValue}px`,
          );
          this.proposedStartSize = this.startSizeValue;
          stopDrag();
          return;
        }
      } else {
        // Not snapable: clamp to minWidth
        if (newSize < this.startMinSizeValue) newSize = this.startMinSizeValue;
      }

      this.proposedStartSize = newSize;
      requestAnimationFrame(this.boundUpdateSize);
    };

    document.addEventListener("mousemove", dragStart);
    document.addEventListener("mouseup", stopDrag);
  }

  // --- END PANEL DRAG ---
  startEnd(event) {
    event.preventDefault();
    const snapable = this.isSnapable("end");
    // CHANGED: orientation-aware pointer and size
    let initialPos = this.isVertical ? event.clientY : event.clientX;
    let initialEndSize = parseInt(
      getComputedStyle(this.endPanelTarget)[
        this.isVertical ? "height" : "width"
      ],
      10,
    );
    this.dragEndTarget.classList.add("dragging");
    const startedAt = Date.now();

    const stopDrag = () => {
      this.dragEndTarget.classList.remove("dragging");
      document.removeEventListener("mousemove", dragEnd);
      document.removeEventListener("mouseup", stopDrag);
      if (snapable && Date.now() - startedAt < 200) this.toggleEndPanel();
    };

    const dragEnd = (event) => {
      // CHANGED: orientation-aware pointer
      let pointer = this.isVertical ? event.clientY : event.clientX;
      let newSize = initialEndSize + (initialPos - pointer);

      if (snapable) {
        // Snap closed
        if (newSize < this.snapCloseFinalValue) {
          this.element.classList.add("end-panel-snapped");
          this.element.style.setProperty(
            "--end-panel-size",
            `${this.endClosedSizeValue}px`,
          );
          this.proposedEndSize = this.endClosedSizeValue;
          stopDrag();
          return;
        }
        // Snap open
        if (
          this.element.classList.contains("end-panel-snapped") &&
          newSize > this.snapOpenThresholdValue
        ) {
          this.element.classList.remove("end-panel-snapped");
          this.element.style.setProperty(
            "--end-panel-size",
            `${this.endSizeValue}px`,
          );
          this.proposedEndSize = this.endSizeValue;
          stopDrag();
          return;
        }
      } else {
        // Not snapable: clamp to minWidth
        if (newSize < this.endMinSizeValue) newSize = this.endMinSizeValue;
      }

      this.proposedEndSize = newSize;
      requestAnimationFrame(this.boundUpdateSize);
    };

    document.addEventListener("mousemove", dragEnd);
    document.addEventListener("mouseup", stopDrag);
  }
}
